import { gql } from '@apollo/client';

export const GET_ORGANIZATION_UNITS = gql`
  query GetOrganizationUnits(
    $first: Int
    $after: String
    $organizationId: UUID
    $where: OrganizationUnitFilterInput
  ) {
    organizationUnits(
      first: $first
      after: $after
      organizationId: $organizationId
      where: $where
    ) {
      nodes {
        id
        name
        description
        sort
        branchType
        appType
        createdAt
        updatedAt
        organization {
          id
          name
        }
        tenant {
          id
          name
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_ORGANIZATION_UNIT = gql`
  query GetOrganizationUnit($id: UUID!) {
    organizationUnit(id: $id) {
      id
      name
      description
      sort
      branchType
      appType
      additionalPropertiesJson
      databaseId
      createdAt
      updatedAt
      organizationId
      tenantId
      organization {
        id
        name
      }
      tenant {
        id
        name
      }
    }
  }
`;

/** Addresses via organizations; use when the root `organizationAddresses` connection returns no rows without a filter. */
export const GET_ORGANIZATIONS_FOR_ADDRESS_LIST = gql`
  query GetOrganizationsForAddressList($first: Int, $after: String, $tenantId: UUID) {
    organizations(first: $first, after: $after, tenantId: $tenantId) {
      nodes {
        id
        name
        isDefault
        addresses {
          id
          type
          isDefault
          addressLine1
          addressLine2
          city
          zipCode
          country
          phoneNumber
          email
          description
          latitude
          longitude
          status
          createdAt
          updatedAt
          organization {
            id
            name
          }
          tenant {
            id
            name
          }
          organizationUnit {
            id
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_ORGANIZATION_ADDRESSES = gql`
  query GetOrganizationAddresses(
    $first: Int
    $after: String
    $where: OrganizationAddressFilterInput
  ) {
    organizationAddresses(first: $first, after: $after, where: $where) {
      nodes {
        id
        type
        isDefault
        addressLine1
        addressLine2
        city
        zipCode
        country
        phoneNumber
        email
        description
        latitude
        longitude
        status
        createdAt
        updatedAt
        organization {
          id
          name
        }
        tenant {
          id
          name
        }
        organizationUnit {
          id
          name
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_ORGANIZATION_ADDRESS = gql`
  query GetOrganizationAddress($id: UUID!) {
    organizationAddress(id: $id) {
      id
      type
      isDefault
      addressLine1
      addressLine2
      city
      zipCode
      country
      phoneNumber
      email
      description
      latitude
      longitude
      status
      organizationId
      organizationUnitId
      tenantId
      createdAt
      updatedAt
      organization {
        id
        name
      }
      organizationUnit {
        id
        name
      }
      tenant {
        id
        name
      }
    }
  }
`;

export const GET_ORGANIZATION_CONTACTS = gql`
  query GetOrganizationContacts(
    $first: Int
    $after: String
    $where: OrganizationContactFilterInput
  ) {
    organizationContacts(first: $first, after: $after, where: $where) {
      nodes {
        id
        firstName
        lastName
        email
        position
        salutation
        createdAt
        updatedAt
        organization {
          id
          name
        }
        tenant {
          id
          name
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_ORGANIZATION_CONTACT = gql`
  query GetOrganizationContact($id: UUID!) {
    organizationContact(id: $id) {
      id
      firstName
      lastName
      email
      position
      salutation
      organizationId
      tenantId
      createdAt
      updatedAt
      organization {
        id
        name
      }
      tenant {
        id
        name
      }
    }
  }
`;

export const GET_PAYMENT_GATEWAYS_LIST = gql`
  query GetPaymentGatewaysList($first: Int, $after: String, $where: PaymentGatewayFilterInput) {
    paymentGateways(first: $first, after: $after, where: $where) {
      nodes {
        id
        name
        provider
        isActive
        isDefault
        createdAt
        updatedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_PAYMENT_GATEWAY = gql`
  query GetPaymentGateway($id: UUID!) {
    paymentGateway(id: $id) {
      id
      name
      provider
      isActive
      isDefault
      createdAt
      updatedAt
    }
  }
`;

export const GET_PAYMENT_TERMS_LIST = gql`
  query GetPaymentTermsList($first: Int, $after: String, $where: PaymentTermFilterInput) {
    paymentTerms(first: $first, after: $after, where: $where) {
      nodes {
        id
        name
        days
        isActive
        isDefault
        createdAt
        updatedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_PAYMENT_TERM = gql`
  query GetPaymentTerm($id: UUID!) {
    paymentTerm(id: $id) {
      id
      name
      days
      isActive
      isDefault
      createdAt
      updatedAt
    }
  }
`;

export const GET_PLANS_LIST = gql`
  query GetPlansList($first: Int, $after: String, $where: PlanFilterInput) {
    plans(first: $first, after: $after, where: $where) {
      nodes {
        id
        name
        code
        contractTermMonths
        amountNet
        amountGross
        createdAt
        updatedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_PLAN_DETAIL = gql`
  query GetPlanDetail($id: UUID!) {
    plan(id: $id) {
      id
      name
      code
      contractTermMonths
      amountNet
      amountGross
      createdAt
      updatedAt
      skus
    }
  }
`;

export const GET_PRODUCTS_LIST = gql`
  query GetProductsList($first: Int, $after: String, $where: ProductFilterInput) {
    products(first: $first, after: $after, where: $where) {
      nodes {
        id
        name
        number
        type
        sku
        shortDescription
        sort
        createdAt
        updatedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_PRODUCT_DETAIL = gql`
  query GetProductDetail($id: UUID!) {
    product(id: $id) {
      id
      name
      number
      type
      sku
      shortDescription
      longDescription
      sort
      createdAt
      updatedAt
    }
  }
`;

export const GET_TAX_RULES_LIST = gql`
  query GetTaxRulesList($first: Int, $after: String, $where: TaxRuleFilterInput) {
    taxRules(first: $first, after: $after, where: $where) {
      nodes {
        id
        name
        taxRate
        isActive
        isDefault
        printText
        createdAt
        updatedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_TAX_RULE = gql`
  query GetTaxRule($id: UUID!) {
    taxRule(id: $id) {
      id
      name
      taxRate
      isActive
      isDefault
      printText
      createdAt
      updatedAt
    }
  }
`;

const entityErr = `
        errors {
          __typename
          ... on EntityNotFoundError {
            message
          }
          ... on EntityReferencedError {
            message
          }
        }
`;

export const CREATE_ORGANIZATION_UNIT = gql`
  mutation CreateOrganizationUnit($input: CreateOrganizationUnitInput!) {
    createOrganizationUnit(input: $input) {
      organizationUnit {
        id
        name
        description
        sort
        branchType
        appType
        additionalPropertiesJson
        databaseId
        organizationId
        tenantId
        sort
        createdAt
        updatedAt
        organization {
          id
          name
        }
        tenant {
          id
          name
        }
      }
    }
  }
`;

export const UPDATE_ORGANIZATION_UNIT = gql`
  mutation UpdateOrganizationUnit($input: UpdateOrganizationUnitInput!) {
    updateOrganizationUnit(input: $input) {
      organizationUnit {
        id
        name
        description
        sort
        branchType
        appType
        additionalPropertiesJson
        databaseId
        organizationId
        tenantId
        createdAt
        updatedAt
      }
      ${entityErr}
    }
  }
`;

export const DELETE_ORGANIZATION_UNIT = gql`
  mutation DeleteOrganizationUnit($input: DeleteOrganizationUnitInput!) {
    deleteOrganizationUnit(input: $input) {
      boolean
      ${entityErr}
    }
  }
`;

export const CREATE_ORGANIZATION_ADDRESS = gql`
  mutation CreateOrganizationAddress($input: CreateOrganizationAddressInput!) {
    createOrganizationAddress(input: $input) {
      organizationAddress {
        id
        type
        isDefault
        addressLine1
        addressLine2
        city
        zipCode
        country
        phoneNumber
        email
        description
        latitude
        longitude
        status
        organizationId
        organizationUnitId
        tenantId
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_ORGANIZATION_ADDRESS = gql`
  mutation UpdateOrganizationAddress($input: UpdateOrganizationAddressInput!) {
    updateOrganizationAddress(input: $input) {
      organizationAddress {
        id
        type
        isDefault
        addressLine1
        addressLine2
        city
        zipCode
        country
        phoneNumber
        email
        description
        latitude
        longitude
        status
        organizationId
        organizationUnitId
        tenantId
        createdAt
        updatedAt
      }
      ${entityErr}
    }
  }
`;

export const DELETE_ORGANIZATION_ADDRESS = gql`
  mutation DeleteOrganizationAddress($input: DeleteOrganizationAddressInput!) {
    deleteOrganizationAddress(input: $input) {
      boolean
      ${entityErr}
    }
  }
`;

export const CREATE_ORGANIZATION_CONTACT = gql`
  mutation CreateOrganizationContact($input: CreateOrganizationContactInput!) {
    createOrganizationContact(input: $input) {
      organizationContact {
        id
        firstName
        lastName
        email
        position
        salutation
        organizationId
        tenantId
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_ORGANIZATION_CONTACT = gql`
  mutation UpdateOrganizationContact($input: UpdateOrganizationContactInput!) {
    updateOrganizationContact(input: $input) {
      organizationContact {
        id
        firstName
        lastName
        email
        position
        salutation
        organizationId
        tenantId
        createdAt
        updatedAt
      }
      ${entityErr}
    }
  }
`;

export const DELETE_ORGANIZATION_CONTACT = gql`
  mutation DeleteOrganizationContact($input: DeleteOrganizationContactInput!) {
    deleteOrganizationContact(input: $input) {
      boolean
      ${entityErr}
    }
  }
`;

export const CREATE_PAYMENT_GATEWAY = gql`
  mutation CreatePaymentGateway($input: CreatePaymentGatewayInput!) {
    createPaymentGateway(input: $input) {
      paymentGateway {
        id
        name
        provider
        isActive
        isDefault
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_PAYMENT_GATEWAY = gql`
  mutation UpdatePaymentGateway($input: UpdatePaymentGatewayInput!) {
    updatePaymentGateway(input: $input) {
      paymentGateway {
        id
        name
        provider
        isActive
        isDefault
        createdAt
        updatedAt
      }
      ${entityErr}
    }
  }
`;

export const DELETE_PAYMENT_GATEWAY = gql`
  mutation DeletePaymentGateway($input: DeletePaymentGatewayInput!) {
    deletePaymentGateway(input: $input) {
      boolean
      ${entityErr}
    }
  }
`;

export const CREATE_PAYMENT_TERM = gql`
  mutation CreatePaymentTerm($input: CreatePaymentTermInput!) {
    createPaymentTerm(input: $input) {
      paymentTerm {
        id
        name
        days
        isActive
        isDefault
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_PAYMENT_TERM = gql`
  mutation UpdatePaymentTerm($input: UpdatePaymentTermInput!) {
    updatePaymentTerm(input: $input) {
      paymentTerm {
        id
        name
        days
        isActive
        isDefault
        createdAt
        updatedAt
      }
      ${entityErr}
    }
  }
`;

export const DELETE_PAYMENT_TERM = gql`
  mutation DeletePaymentTerm($input: DeletePaymentTermInput!) {
    deletePaymentTerm(input: $input) {
      boolean
      ${entityErr}
    }
  }
`;

export const CREATE_PLAN = gql`
  mutation CreatePlan($input: CreatePlanInput!) {
    createPlan(input: $input) {
      plan {
        id
        name
        code
        contractTermMonths
        amountNet
        amountGross
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_PLAN = gql`
  mutation UpdatePlan($input: UpdatePlanInput!) {
    updatePlan(input: $input) {
      plan {
        id
        name
        code
        amountNet
        amountGross
        contractTermMonths
        createdAt
        updatedAt
      }
      ${entityErr}
    }
  }
`;

// export const DELETE_PLAN = gql`
//   mutation DeletePlan($input: DeletePlanInput!) {
//     deletePlan(input: $input) {
//       boolean
//       ${entityErr}
//     }
//   }
// `;

export const CREATE_PRODUCT = gql`
  mutation CreateProduct($input: CreateProductInput!) {
    createProduct(input: $input) {
      product {
        id
        name
        number
        type
        sku
        sort
        shortDescription
        longDescription
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_PRODUCT = gql`
  mutation UpdateProduct($input: UpdateProductInput!) {
    updateProduct(input: $input) {
      product {
        id
        name
        number
        type
        sku
        sort
        shortDescription
        longDescription
        createdAt
        updatedAt
      }
      ${entityErr}
    }
  }
`;

// export const DELETE_PRODUCT = gql`
//   mutation DeleteProduct($input: DeleteProductInput!) {
//     deleteProduct(input: $input) {
//       boolean
//       ${entityErr}
//     }
//   }
// `;

export const CREATE_TAX_RULE = gql`
  mutation CreateTaxRule($input: CreateTaxRuleInput!) {
    createTaxRule(input: $input) {
      taxRule {
        id
        name
        taxRate
        isActive
        isDefault
        printText
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_TAX_RULE = gql`
  mutation UpdateTaxRule($input: UpdateTaxRuleInput!) {
    updateTaxRule(input: $input) {
      taxRule {
        id
        name
        taxRate
        isActive
        isDefault
        printText
        createdAt
        updatedAt
      }
      ${entityErr}
    }
  }
`;

export const DELETE_TAX_RULE = gql`
  mutation DeleteTaxRule($input: DeleteTaxRuleInput!) {
    deleteTaxRule(input: $input) {
      boolean
      ${entityErr}
    }
  }
`;

export const GET_APP_VERSIONS_LIST = gql`
  query GetAppVersionsList($first: Int, $after: String, $where: AppVersionFilterInput) {
    appVersions(first: $first, after: $after, where: $where) {
      nodes {
        id
        versionName
        versionCode
        releaseNotes
        releaseDate
        isActive
        createdAt
        updatedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_APP_VERSION_DETAIL = gql`
  query GetAppVersionDetail($id: UUID!) {
    appVersion(id: $id) {
      id
      versionName
      versionCode
      releaseNotes
      releaseDate
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const GET_APP_VERSION_POLICY = gql`
  query GetAppVersionPolicy {
    appVersionPolicy {
      requiredMinVersion
      suggestedMinVersion
      updateStoreUrl
    }
  }
`;

export const CREATE_APP_VERSION = gql`
  mutation CreateAppVersion($input: CreateAppVersionInput!) {
    createAppVersion(input: $input) {
      appVersion {
        id
        versionName
        versionCode
        releaseNotes
        releaseDate
        isActive
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_APP_VERSION = gql`
  mutation UpdateAppVersion($input: UpdateAppVersionInput!) {
    updateAppVersion(input: $input) {
      appVersion {
        id
        versionName
        versionCode
        releaseNotes
        releaseDate
        isActive
        createdAt
        updatedAt
      }
      ${entityErr}
    }
  }
`;

export const DELETE_APP_VERSION = gql`
  mutation DeleteAppVersion($input: DeleteAppVersionInput!) {
    deleteAppVersion(input: $input) {
      boolean
      ${entityErr}
    }
  }
`;

export const UPDATE_APP_VERSION_POLICY = gql`
  mutation UpdateAppVersionPolicy($input: UpdateAppVersionPolicyInput!) {
    updateAppVersionPolicy(input: $input) {
      appVersionPolicy {
        requiredMinVersion
        suggestedMinVersion
        updateStoreUrl
      }
    }
  }
`;

export const UPDATE_TENANT_DISCOUNT_PERCENT = gql`
  mutation UpdateTenantDiscountPercent($input: UpdateTenantDiscountPercentInput!) {
    updateTenantDiscountPercent(input: $input) {
      tenant {
        id
        discountPercent
      }
    }
  }
`;

export const UPDATE_TENANT_RESELLER_LOGO = gql`
  mutation UpdateTenantResellerLogo($input: UpdateTenantResellerLogoInput!) {
    updateTenantResellerLogo(input: $input) {
      tenant {
        id
        resellerLogo
      }
      errors {
        ... on EntityNotFoundError {
          message
        }
        ... on InvalidOperationError {
          message
        }
      }
    }
  }
`;

export const CREATE_PROVISION_PAYOUT = gql`
  mutation CreateProvisionPayout($input: CreateProvisionPayoutInput!) {
    createProvisionPayout(input: $input) {
      accountingDocument {
        id
        number
        amountNet
        amountGross
        state
        createdAt
      }
    }
  }
`;

export const GET_COUPONS = gql`
  query GetCoupons($first: Int, $after: String, $where: CouponFilterInput) {
    coupons(first: $first, after: $after, where: $where) {
      nodes {
        id
        code
        name
        description
        type
        duration
        discountType
        discountValue
        status
        usageType
        maxRedemptions
        redemptionCount
        expiryDate
        allowedPlanCodes
        allowedProductSkus
        createdAt
        updatedAt
      }
    }
  }
`;
export const GET_AVAILABLE_COUPONS = gql`
  query GetAvailableCoupons(
    $first: Int
    $after: String
    $where: CouponFilterInput
    $order: [CouponSortInput!]
  ) {
    availableCoupons(first: $first, after: $after, where: $where, order: $order) {
      nodes {
        id
        code
        name
        description
        type
        duration
        discountType
        discountValue
        status
        maxRedemptions
        redemptionCount
        expiryDate
        allowedPlanCodes
        allowedProductSkus
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_COUPON = gql`
  query GetCoupon($id: UUID!) {
    coupon(id: $id) {
      id
      code
      name
      description
      type
      duration
      discountType
      discountValue
      status
      usageType
      maxRedemptions
      redemptionCount
      expiryDate
      allowedPlanCodes
      allowedProductSkus
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_COUPON = gql`
  mutation CreateCoupon($input: CreateCouponInput!) {
    createCoupon(input: $input) {
      coupon {
        id
      }
    }
  }
`;

export const UPDATE_COUPON = gql`
  mutation UpdateCoupon($input: UpdateCouponInput!) {
    updateCoupon(input: $input) {
      coupon {
        id
      }
      ${entityErr}
    }
  }
`;

export const DELETE_COUPON = gql`
  mutation DeleteCoupon($input: DeleteCouponInput!) {
    deleteCoupon(input: $input) {
      boolean
      ${entityErr}
    }
  }
`;
export const MAIN_COUPON = gql`
  query MainCoupon {
    mainCoupon {
      id
      code
      discountPercent
    }
  }
`;
export const UPDATE_MAIN_COUPON = gql`
  mutation UpdateMainCoupon($input: UpdateMainCouponInput!) {
    updateMainCoupon(input: $input) {
      boolean
    }
  }
`;
export const DELETE_MAIN_COUPON = gql`
  mutation DeleteMainCoupon {
    deleteMainCoupon {
      boolean
    }
  }
`;

const RESOURCE_FIELDS = gql`
  fragment ResourceFields on Resource {
    id
    title
    fileName
    contentType
    previewBase64
    fileSizeBytes
    sortOrder
    createdAt
    updatedAt
  }
`;

export const GET_RESOURCES = gql`
  ${RESOURCE_FIELDS}
  query GetResources {
    resources {
      ...ResourceFields
    }
  }
`;

export const GET_RESOURCE = gql`
  query GetResource($id: UUID!) {
    resource(id: $id) {
      id
      title
      fileName
      contentType
      contentBase64
      previewBase64
      fileSizeBytes
      sortOrder
    }
  }
`;

export const CREATE_RESOURCE = gql`
  ${RESOURCE_FIELDS}
  mutation CreateResource($input: CreateResourceInput!) {
    createResource(input: $input) {
      resource {
        ...ResourceFields
      }
      errors {
        ... on InvalidOperationError {
          message
        }
      }
    }
  }
`;

export const UPDATE_RESOURCE = gql`
  ${RESOURCE_FIELDS}
  mutation UpdateResource($input: UpdateResourceInput!) {
    updateResource(input: $input) {
      resource {
        ...ResourceFields
      }
      errors {
        ... on EntityNotFoundError {
          message
        }
        ... on InvalidOperationError {
          message
        }
      }
    }
  }
`;

export const DELETE_RESOURCE = gql`
  mutation DeleteResource($input: DeleteResourceInput!) {
    deleteResource(input: $input) {
      boolean
      errors {
        ... on EntityNotFoundError {
          message
        }
      }
    }
  }
`;

export const REORDER_RESOURCES = gql`
  ${RESOURCE_FIELDS}
  mutation ReorderResources($input: ReorderResourcesInput!) {
    reorderResources(input: $input) {
      resource {
        ...ResourceFields
      }
      errors {
        ... on EntityNotFoundError {
          message
        }
      }
    }
  }
`;

const INVITATION_FIELDS = gql`
  fragment InvitationFields on Invitation {
    id
    tenantId
    token
    state
    recipientTenantId
    recipientEmail
    recipientName
    message
    createdAt
    updatedAt
    respondedAt
  }
`;

const invitationStateErr = `
        errors {
          __typename
          ... on EntityNotFoundError {
            message
          }
          ... on InvalidStateTransitionError {
            message
          }
        }
`;

export const GET_INVITATIONS = gql`
  ${INVITATION_FIELDS}
  query GetInvitations($first: Int, $after: String, $where: InvitationFilterInput) {
    invitations(first: $first, after: $after, where: $where) {
      nodes {
        ...InvitationFields
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const CREATE_INVITATION = gql`
  ${INVITATION_FIELDS}
  mutation CreateInvitation($input: CreateInvitationInput!) {
    createInvitation(input: $input) {
      invitation {
        ...InvitationFields
      }
    }
  }
`;

export const RESEND_INVITATION = gql`
  ${INVITATION_FIELDS}
  mutation ResendInvitation($input: ResendInvitationInput!) {
    resendInvitation(input: $input) {
      invitation {
        ...InvitationFields
      }
      ${invitationStateErr}
    }
  }
`;

export const MARK_INVITATION_FAILED = gql`
  ${INVITATION_FIELDS}
  mutation MarkInvitationFailed($input: MarkInvitationFailedInput!) {
    markInvitationFailed(input: $input) {
      invitation {
        ...InvitationFields
      }
      ${invitationStateErr}
    }
  }
`;

export const REVOKE_INVITATION = gql`
  mutation RevokeInvitation($input: RevokeInvitationInput!) {
    revokeInvitation(input: $input) {
      boolean
      ${invitationStateErr}
    }
  }
`;
